import { titleCase } from "../../../../../utils";
import { TDocument, TDocumentData } from "../../../models/document";
import { TVerificationAppElement } from "../../../../app/models";
import { TUserFullInfo } from "../../../models";
import { PdfSection, PdfRow, PdfAttachment } from "./types";
import { serializeField } from "./fieldSerializers";

export type DefaultSectionContext = {
  userInfo: TUserFullInfo;
  comments: Record<string, string>;
  countriesRegex: any;
  companyPurpose: string;
};

/**
 * Builds a PdfSection from a document using config fields when available, otherwise raw key/value.
 * Only the main binary entry (dataId === documentType) is serialized; report entries are ignored.
 */
export function buildDefaultSection(
  document: TDocument,
  stepConfig: TVerificationAppElement | null,
  context: DefaultSectionContext
): PdfSection {
  const binary: TDocumentData[] = [];
  const files: TDocumentData[] = [];
  for (const item of document.data) {
    if (item.type === "binary") binary.push(item);
    else if (item.type === "file") files.push(item);
  }

  const documentType = document.document.documentType;
  const mainEntry = binary.find((d) => d.dataId === documentType);

  const rows: PdfRow[] = [];
  const attachmentsFromFields: PdfAttachment[] = [];

  if (mainEntry) {
    const data = (mainEntry.data ?? {}) as Record<string, unknown>;
    if (stepConfig?.fields?.length) {
      for (const field of stepConfig.fields) {
        const result = serializeField(
          field,
          data,
          files,
          context.countriesRegex
        );
        rows.push(...result.rows);
        attachmentsFromFields.push(...result.attachments);
      }
    } else {
      for (const key of Object.keys(data)) {
        const value = data[key];
        if (value != null && value !== "") {
          rows.push({ label: titleCase(key), value: String(value) });
        }
      }
    }
  }

  const comment = context.comments[document.document.documentId];
  if (comment) {
    rows.push({ label: "Comment", value: comment });
  }

  const matchedFileIds = new Set(
    attachmentsFromFields.map((a) => a.file.id)
  );
  const remainingFiles = files.filter((f) => !matchedFileIds.has(f.id));
  const attachments: PdfAttachment[] = [
    ...attachmentsFromFields,
    ...remainingFiles.map((file) => ({ label: file.dataId, file })),
  ];

  return {
    title: titleCase(document.document.documentType),
    document,
    rows,
    attachments,
  };
}
